import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { ExportFormat } from "../modules/shared/components/export-button/export-button.component";
import {
    KeyValuePair,
    PagedList
} from "../modules/shared/models/api-response.model";
import { buildExportFilename } from "../modules/shared/utils/export-helpers";
import { AuthService, Role } from "./auth.service";

const API_URL = environment.api;

export type UserDepartmentType = {
    code: string;
    name: string;
    accountId: string;
    display: boolean;
    checked: boolean;
};

export interface IRole {
    roleId: number;
    name: string;
}

export interface IUserDepartment {
    accountId: string;
    departmentTypeName: number;
    departmentTypeCode: string;
}

export interface IUser {
    accounts: { accountId: string; accountName: string }[];
    accountId: string;
    accountName: string;
    avatar: string | null;
    createdAt: Date;
    email: string;
    firstName: string;
    isDeleted: boolean;
    lastLoginAt: Date | null;
    lastName: string;
    firstLast: string;
    lastFirst: string;
    phone: string | null;
    secondaryPhone: string | null;
    roles: Role[];
    userDepartmentTypes: IUserDepartment[];
    jobTitle: string | null;
    userId: number;
    password: string | null;
    isAccountDeleted: boolean;
    emailNotificationOptions: { key: string }[];
    emailNotificationNames: string;
    accountContactRoles: string[];
    hasLiveAccounts: boolean;
}

@Injectable()
export class UserService {
    constructor(
        public http: HttpClient,
        private _authService: AuthService
    ) {}

    private get _defaultOptions() {
        return { headers: this._headers };
    }
    private get _headers(): HttpHeaders {
        let h = new HttpHeaders();
        h = h.set("Authorization", `bearer ${this._authService.accessToken}`);
        h = h.set("Content-Type", "application/json");
        return h;
    }

    public changes: Subject<any> = new BehaviorSubject(null);

    getISIContacts(): Observable<IUser[]> {
        return this.http
            .get(`${API_URL}/api/v1/users/isi-contacts`, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    res = res.map((u: IUser) => {
                        return this._mapUser(u);
                    });

                    return res;
                })
            );
    }

    getAll(
        offset = 0,
        limit = 10,
        sort: string | null = "+firstName",
        search: string | null = null,
        accounts: string[] | null = null,
        roles: string[] | null = null,
        emailOptions: string[] | null = null,
        departments: string[] | null = null,
        accountStatus: string[] | null = null,
        userStatus: ("Active" | "Inactive")[] | null = ["Active"]
    ): Observable<PagedList<IUser>> {
        const p = {};

        if (limit !== -1) {
            p["offset"] = `${offset * limit}`;
            p["limit"] = `${limit}`;
        }

        if (search) {
            p["filter"] = search;
        }

        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }

        if (sort) {
            if (sort.charAt(0) === "+") sort = sort.substr(1, sort.length);
            p["sort"] = sort;
        } else {
            sort = "+firstName";
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        }

        if (roles && roles.length > 0) {
            p["roles"] = roles.join(",");
        }

        if (emailOptions && emailOptions.length > 0) {
            p["email-options"] = emailOptions.join(",");
        }

        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }

        if (accountStatus && accountStatus.length > 0) {
            p["account-status"] = accountStatus.join(",");
        }

        if (userStatus && userStatus.length > 0) {
            p["user-status"] = userStatus.join(",");
        }

        const options = { headers: this._headers, params: p };

        return this.http.get(`${API_URL}/api/v1/users`, options).pipe(
            map((res: PagedList<IUser>) => {
                res.offset = offset;
                res.data = res.data.map((u: IUser) => {
                    return this._mapUser(u);
                });

                return res;
            })
        );
    }

    exportAll(
        columns: unknown[],
        format: ExportFormat,
        sort: string | null = null,
        search: string | null = null,
        accounts: string[] | null = null,
        roles: string[] | null = null,
        emailOptions: string[] | null = null,
        departments: string[] | null = null,
        accountStatus: string[] | null = null,
        userStatus: ("Active" | "Inactive")[] | null = ["Active"]
    ): Observable<Blob> {
        const p = {
            format
        };

        if (search) {
            p["filter"] = search;
        }

        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }

        if (sort) {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        } else {
            p["sort"] = "firstName";
        }

        if (roles && roles.length > 0) {
            p["roles"] = roles.join(",");
        }

        if (emailOptions && emailOptions.length > 0) {
            p["email-options"] = emailOptions.join(",");
        }

        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }

        if (accountStatus && accountStatus.length > 0) {
            p["account-status"] = accountStatus.join(",");
        }

        if (userStatus && userStatus.length > 0) {
            p["user-status"] = userStatus.join(",");
        }

        const options = {
            headers: this._headers,
            params: p,
            responseType: "blob" as "json"
        };

        return this.http
            .post<Blob>(`${API_URL}/api/v1/users/export`, columns, options)
            .pipe(
                map((res) => {
                    const url = window.URL.createObjectURL(res);
                    const a = document.createElement("a");
                    document.body.appendChild(a);
                    a.setAttribute("style", "display: none");
                    a.href = url;
                    a.download = buildExportFilename("users_export", this._authService.claims.accountName, format);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();

                    return res;
                })
            );
    }

    getUser(id: number): Observable<IUser> {
        return this.http
            .get(`${API_URL}/api/v1/users/${id}`, this._defaultOptions)
            .pipe(
                map((res: IUser) => {
                    return this._mapUser(res);
                })
            );
    }

    getUserAvatar(id: number): Observable<string> {
        let h = this._headers;
        h.set("Content-Type", "text/plain");
        let options = { headers: h };
        return this.http
            .get(`${API_URL}/api/v1/users/${id}/avatar`, {
                ...options,
                responseType: "text"
            })
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    updateUser(user: IUser): Observable<IUser> {
        return this.http
            .put(
                `${API_URL}/api/v1/users/${user.userId}`,
                user,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    this.changes.next(null);
                    return this._mapUser(res);
                })
            );
    }

    deleteUser(userId: number): Observable<any> {
        return this.http
            .delete(`${API_URL}/api/v1/users/${userId}`, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    this.changes.next(null);
                    return res;
                })
            );
    }

    undeleteUser(userId: number): Observable<unknown> {
        return this.http.post(
            `${API_URL}/api/v1/users/${userId}/undelete`,
            null,
            this._defaultOptions
        );
    }

    createUser(user: IUser): Observable<IUser> {
        user.password = null;
        return this.http
            .post(`${API_URL}/api/v1/users`, user, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    this.changes.next(null);
                    return this._mapUser(res);
                })
            );
    }

    addRoles(userId: number, roles: Array<number>): Observable<any> {
        return this.http
            .post(
                `${API_URL}/api/v1/users/${userId}/roles`,
                roles,
                this._defaultOptions
            )
            .pipe(shareReplay());
    }

    getRoleOptions(): Observable<Array<KeyValuePair<number, string>>> {
        const url = `${API_URL}/api/v1/roles/full`;
        return this.http
            .get<Array<KeyValuePair<number, string>>>(url, this._defaultOptions)
            .pipe(shareReplay());
    }

    getEmailNotificationOptions(): Observable<{ value: string; label: string }[]> {
        return this.http
            .get<{ value: string; label: string }[]>(
                `${API_URL}/api/v1/users/notification-options`,
                this._defaultOptions
            )
            .pipe(shareReplay());
    }

    getBranchAccessOptions(
        accountIds: string[] | null = null
    ): Observable<UserDepartmentType[]> {
        let p = {};

        if (accountIds) {
            p["accountIds"] = accountIds.join(",");
        }

        let options = { headers: this._headers, params: p };
        return this.http
            .get(`${API_URL}/api/v1/user-department-types`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.map((x) => {
                        return {
                            code: x.departmentTypeCode,
                            name: x.departmentTypeName,
                            accountId: x.accountId,
                            display: false,
                            checked: false
                        };
                    });
                })
            );
    }

    private _mapUser(u: any): IUser {
        if (u.createdAt) {
            u.createdAt = new Date(u.createdAt);
        }

        if (u.lastLoginAt !== null) {
            u.lastLoginAt = new Date(u.lastLoginAt);
        }

        if (u.accounts) {
            u.accountName =
                u.accounts.length === 1 ? u.accounts[0].accountName : "MULTI";
        }

        u.avatar = u.avatar ? u.avatar : "";
        u.firstLast = `${u.firstName} ${u.lastName}`;
        u.lastFirst = `${u.lastName}, ${u.firstName}`;

        return u;
    }
}
